import { faPause, faPenLine, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { ComponentProps, useState } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { TooltipProps } from '@growtherapy/sprout-ui';

import { ControlPanelButton, ControlPanelButtonTooltip } from '../../components/ControlPanelButton';

type ControlPanelButtonProps = Omit<
  ComponentProps<typeof ControlPanelButton>,
  'aria-label' | 'icon' | 'iconName' | 'iconVariant' | 'iconDefinition'
>;
type RecordingControlProps = ControlPanelButtonProps & {
  isRecording?: boolean;
  isRequestingConsent?: boolean;
  consentPending: boolean;
  onRecordingToggle: (shouldRecord: boolean) => void | Promise<void>;
  onPendingConsentClick?: () => void | Promise<void>;
  tooltipProps?: Partial<TooltipProps>;
  onRecordingToggleError?: (message: string) => void;
};

export function RecordingControl({
  isRecording,
  isRequestingConsent,
  consentPending,
  onRecordingToggle,
  onPendingConsentClick,
  tooltipProps,
  onRecordingToggleError,
  ...props
}: RecordingControlProps) {
  const t = useTranslate();
  const [isLoading, setIsLoading] = useState(false);

  let buttonLabel = isRecording
    ? t('telehealth-core:recording.toggleRecordingOff')
    : t('telehealth-core:recording.toggleRecordingOn');
  let buttonIcon = isRecording ? faPause : faPlay;

  if (consentPending) {
    buttonLabel = t('telehealth-core:recording.requestTranscriptionConsent');
    buttonIcon = faPenLine;
  }

  if (isRequestingConsent) {
    buttonLabel = t('telehealth-core:recording.requestingClientConsent');
  }

  return (
    <ControlPanelButtonTooltip {...tooltipProps} text={tooltipProps?.text ?? buttonLabel} childIsInteractive>
      <ControlPanelButton
        {...props}
        loading={isLoading || isRequestingConsent}
        aria-label={buttonLabel}
        iconDefinition={buttonIcon}
        data-testid='recording-control'
        onClick={async () => {
          if (!consentPending) {
            try {
              setIsLoading(true);
              await onRecordingToggle(!isRecording);
            } catch (error) {
              onRecordingToggleError?.(t('telehealth-core:recording.somethingWentWrongWhileTogglingRecording'));
            } finally {
              setIsLoading(false);
            }
          } else if (onPendingConsentClick) {
            await onPendingConsentClick();
          }
        }}
      />
    </ControlPanelButtonTooltip>
  );
}
